import Logo from '@/components/layout/Logo'
import { lumi } from '@/lib/lumi'
import {AlertCircle, Loader2, Mail, X} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

interface AuthModalProps {
  open: boolean
  onClose: () => void
}

type Mode = 'signin' | 'signup' | 'reset'

function errorMessage(e: any): string {
  const code = e?.data?.code
  const knownMessages: Record<number, string> = {
    2254: 'That email is not eligible to register.',
    2259: 'This account is not active. Please contact support.',
    2261: 'Email login is currently disabled for this account.',
    2262: 'Google login is currently disabled for this account.',
  }
  return knownMessages[code] || e?.message || 'Something went wrong. Please try again.'
}

function AuthModal({ open, onClose }: AuthModalProps) {
  const [mode, setMode] = useState<Mode>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [otp, setOtp] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [otpSending, setOtpSending] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const navigate = useNavigate()

  useEffect(() => {
    if (countdown <= 0) return
    const t = setTimeout(() => setCountdown(c => c - 1), 1000)
    return () => clearTimeout(t)
  }, [countdown])

  useEffect(() => {
    if (!open) {
      setMode('signin')
      setEmail('')
      setPassword('')
      setOtp('')
      setError('')
      setLoading(false)
      setCountdown(0)
    }
  }, [open])

  if (!open) return null

  const switchMode = (next: Mode) => {
    setMode(next)
    setError('')
    setOtp('')
  }

  const handleSendOtp = async () => {
    if (!email) {
      setError('Please enter your email first.')
      return
    }
    setOtpSending(true)
    setError('')
    try {
      await lumi.auth.sendOtp({ email, type: mode === 'reset' ? 'resetPassword' : 'register' })
      setCountdown(60)
    } catch (e: any) {
      setError(errorMessage(e))
    } finally {
      setOtpSending(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      if (mode === 'signin') {
        await lumi.auth.signInWithPassword({ email, password })
        navigate('/dashboard')
      } else if (mode === 'signup') {
        await lumi.auth.signUpWithPassword({ email, password, otp })
        navigate('/dashboard')
      } else {
        await lumi.auth.resetPassword({ email, newPassword: password, otp })
        navigate('/dashboard')
      }
      onClose()
    } catch (e: any) {
      setError(errorMessage(e))
    } finally {
      setLoading(false)
    }
  }

  const handleGoogle = async () => {
    setError('')
    setLoading(true)
    try {
      await lumi.auth.signInWithOAuth({ provider: 'google' })
      navigate('/dashboard')
      onClose()
    } catch (e: any) {
      setError(errorMessage(e))
    } finally {
      setLoading(false)
    }
  }

  const titles: Record<Mode, string> = {
    signin: 'Welcome back, trainer',
    signup: 'Join the open beta',
    reset: 'Reset your password',
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/80 px-3 py-4 backdrop-blur-sm sm:items-center sm:px-4">
      <div className="relative mb-16 max-h-[calc(100dvh-6rem)] w-full max-w-md overflow-y-auto rounded-[32px] border-4 border-[#3B4CCA] bg-[#111111] p-5 shadow-[10px_10px_0_rgba(255,26,26,0.28)] sm:mb-0 sm:p-6">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-1.5 text-white/50 hover:bg-white/5 hover:text-white transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="mb-6">
          <Logo className="mb-4 justify-center" size="auth" />
          <h2 className="font-display text-center text-2xl font-extrabold text-white">{titles[mode]}</h2>
          {mode === 'signup' ? (
            <p className="mt-2 text-center text-sm font-semibold text-white/55">
              Free open beta. No payment needed to start your store.
            </p>
          ) : null}
        </div>

        {error ? (
          <div className="mb-4 flex items-start gap-2 rounded-2xl border-2 border-[#FF1A1A]/50 bg-[#FF1A1A]/12 px-3 py-2.5 text-sm font-semibold text-[#FF5A5A]">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-white/60">Email</label>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
              <input
                type="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full rounded-2xl border-2 border-white/10 bg-white/[0.03] py-2.5 pl-10 pr-3 text-sm font-semibold text-white placeholder:text-white/25 outline-none focus:border-[#FFDE00] focus:ring-2 focus:ring-[#FFDE00]/20"
              />
            </div>
          </div>

          {(mode === 'signup' || mode === 'reset') && (
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-white/60">Verification code</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  required
                  value={otp}
                  onChange={e => setOtp(e.target.value)}
                  placeholder="6-digit code"
                  className="flex-1 rounded-2xl border-2 border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm font-semibold text-white placeholder:text-white/25 outline-none focus:border-[#FFDE00] focus:ring-2 focus:ring-[#FFDE00]/20"
                />
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={otpSending || countdown > 0}
                  className="shrink-0 rounded-full border-2 border-[#FFDE00] bg-[#FFDE00]/10 px-3 py-2.5 text-xs font-extrabold text-[#FFDE00] transition-transform hover:-translate-y-0.5 disabled:opacity-50"
                >
                  {countdown > 0 ? `${countdown}s` : otpSending ? '...' : 'Send code'}
                </button>
              </div>
            </div>
          )}

          <div>
            <label className="mb-1.5 block text-xs font-semibold text-white/60">
              {mode === 'reset' ? 'New password' : 'Password'}
            </label>
            <input
              type="password"
              required
              minLength={6}
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full rounded-2xl border-2 border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm font-semibold text-white placeholder:text-white/25 outline-none focus:border-[#FFDE00] focus:ring-2 focus:ring-[#FFDE00]/20"
            />
          </div>

          {mode === 'signin' && (
            <button
              type="button"
              onClick={() => switchMode('reset')}
              className="-mt-1 self-end text-xs font-semibold text-white/50 hover:text-[#FFDE00]"
            >
              Forgot password?
            </button>
          )}

          <button
            type="submit"
            disabled={loading}
            className="mt-1 flex items-center justify-center gap-2 rounded-full bg-[#FFDE00] py-3 text-sm font-extrabold text-black shadow-[0_5px_0_#c9a800] transition-transform hover:-translate-y-1 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {mode === 'signin' ? 'Sign in' : mode === 'signup' ? 'Create account' : 'Reset password'}
          </button>
        </form>

        {mode !== 'reset' && (
          <>
            <div className="my-4 flex items-center gap-3">
              <div className="h-px flex-1 bg-white/10" />
              <span className="text-xs text-white/35">or</span>
              <div className="h-px flex-1 bg-white/10" />
            </div>
            <button
              type="button"
              onClick={handleGoogle}
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-full border-2 border-white/15 bg-white/[0.03] py-2.5 text-sm font-extrabold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </button>
          </>
        )}

        <p className="mt-5 text-center text-sm text-white/45">
          {mode === 'signin' ? (
            <>
              New to My Card Shop?{' '}
              <button type="button" onClick={() => switchMode('signup')} className="font-bold text-[#FFDE00] hover:text-white">
                Create a store
              </button>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <button type="button" onClick={() => switchMode('signin')} className="font-bold text-[#FFDE00] hover:text-white">
                Sign in
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  )
}

export default AuthModal
